// src/features/operational/components/assessment/AnnualTeachingPlan.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  MenuBook as SubjectIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { useAuth } from '../../../../contexts/AuthContext';
import MathRenderer from '../../../../components/math/MathRenderer';
import {
  createAnnualTeachingPlan,
  createAnnualTeachingPlanPlan,
  createAnnualTeachingPlansBulk,
  deleteAnnualTeachingPlan,
  deleteAnnualTeachingPlanPlan,
  fetchAnnualTeachingPlanPlans,
  fetchAnnualTeachingPlans,
  updateAnnualTeachingPlan,
  updateAnnualTeachingPlanPlan
} from '../../services/assessmentService';
import { fetchUserSubjects } from '../../services/subjectService';

const currentYear = new Date().getFullYear().toString();

const dialogPaperSx = { borderRadius: 2 };

const buildPlanForm = (plan = null) => ({
  title: plan?.title || '',
  subject: plan?.subject?.id || '',
  grade: plan?.grade || '',
  academicYear: plan?.academicYear || currentYear,
  description: plan?.description || ''
});

const normalizeContentItems = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => `${entry}`.trim()).filter(Boolean);
  }

  const trimmed = `${value || ''}`.trim();
  return trimmed ? [trimmed] : [''];
};

const splitContentEntries = (value) => {
  const items = normalizeContentItems(value);

  if (items.length === 1 && /[\n|;]/.test(items[0])) {
    return items[0]
      .split(/[\n|;]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return items.filter(Boolean);
};

const buildItemForm = (plan = null, item = null) => ({
  atpId: item?.atpId || plan?.id || '',
  subject: item?.subject?.id || plan?.subject?.id || '',
  grade: item?.grade || plan?.grade || '',
  topic: item?.topic || '',
  contentItems: normalizeContentItems(item?.content),
  duration: item?.duration || '',
  plannedStart: item?.plannedStart || '',
  plannedFinish: item?.plannedFinish || '',
  actualStart: item?.actualStart || '',
  actualFinish: item?.actualFinish || '',
  term: item?.term || '',
  progress: item?.progress || 'Planned',
  status: item?.status || 'On Schedule'
});

const splitDelimitedLine = (line, delimiter) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const normalizeDate = (value) => {
  if (!value) return '';

  const trimmed = `${value}`.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const slashMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().slice(0, 10);
};

const normalizeTerm = (value) => {
  const match = `${value || ''}`.match(/[1-4]/);
  return match ? match[0] : '';
};

const headerAliases = {
  topic: ['topic', 'name', 'title'],
  content: ['content', 'description'],
  curriculumStatements: ['curriculumstatements', 'curriculum', 'statements', 'curriculumstatement'],
  duration: ['duration'],
  term: ['term', 'quarter'],
  plannedStart: ['plannedstart', 'start', 'startdate', 'plannedstartdate'],
  plannedFinish: ['plannedfinish', 'finish', 'end', 'enddate', 'plannedfinishdate'],
  actualStart: ['actualstart', 'actualstartdate'],
  actualFinish: ['actualfinish', 'actualfinishdate'],
  progress: ['progress'],
  status: ['status']
};

const resolveHeaderKey = (header) => {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  return Object.entries(headerAliases).find(([, aliases]) => aliases.includes(normalized))?.[0] || null;
};

const parseImportedItems = (content, selectedPlan) => {
  const trimmed = content.trim();

  if (!trimmed) {
    return { items: [], errors: ['The selected file is empty.'] };
  }

  let rows = [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      rows = Array.isArray(parsed) ? parsed : [];
    } catch {
      return { items: [], errors: ['The JSON file could not be parsed.'] };
    }
  } else {
    const lines = trimmed.split(/\r?\n/).filter(Boolean);
    const delimiter = lines[0].includes('\t') ? '\t' : ',';
    const rawHeaders = splitDelimitedLine(lines[0], delimiter);
    const headers = rawHeaders.map(resolveHeaderKey);

    rows = lines.slice(1).map((line) => {
      const values = splitDelimitedLine(line, delimiter);
      return headers.reduce((acc, key, index) => {
        if (key) {
          acc[key] = values[index] || '';
        }
        return acc;
      }, {});
    });
  }

  const errors = [];
  const items = rows.reduce((acc, row, index) => {
    const topic = `${row.topic || ''}`.trim();
    const term = normalizeTerm(row.term);
    const plannedStart = normalizeDate(row.plannedStart);
    const plannedFinish = normalizeDate(row.plannedFinish);

    if (!topic || !term || !plannedStart || !plannedFinish) {
      errors.push(`Row ${index + 2}: topic, term, plannedStart, and plannedFinish are required.`);
      return acc;
    }

    acc.push({
      atpId: selectedPlan.id,
      subject: selectedPlan.subject.id,
      grade: selectedPlan.grade,
      topic,
      content: splitContentEntries(row.content),
      curriculumStatements: `${row.curriculumStatements || ''}`
        .split(/[|;]/)
        .map((entry) => entry.trim())
        .filter(Boolean),
      duration: `${row.duration || ''}`.trim(),
      term,
      plannedStart,
      plannedFinish,
      actualStart: normalizeDate(row.actualStart),
      actualFinish: normalizeDate(row.actualFinish),
      progress: `${row.progress || 'Planned'}`.trim() || 'Planned',
      status: `${row.status || 'On Schedule'}`.trim() || 'On Schedule',
      source: 'import'
    });
    return acc;
  }, []);

  return { items, errors };
};

const AnnualTeachingPlan = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [termFilter, setTermFilter] = useState('');
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(buildPlanForm());

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(buildItemForm());
  const [itemDialogMessage, setItemDialogMessage] = useState({ type: '', text: '' });

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  const filteredItems = useMemo(() => (
    items
      .filter((item) => item.atpId === selectedPlanId)
      .filter((item) => !termFilter || item.term === termFilter)
      .filter((item) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const contentText = normalizeContentItems(item.content).join(' ').toLowerCase();
        return (
          item.topic.toLowerCase().includes(search)
          || contentText.includes(search)
          || (item.subject?.name || '').toLowerCase().includes(search)
        );
      })
  ), [items, searchTerm, selectedPlanId, termFilter]);

  const loadData = useCallback(async (preferredPlanId = null) => {
    setLoading(true);
    try {
      const [planData, itemData, subjectData] = await Promise.all([
        fetchAnnualTeachingPlanPlans(currentUser.uid),
        fetchAnnualTeachingPlans(currentUser.uid),
        fetchUserSubjects(currentUser.uid)
      ]);

      setPlans(planData);
      setItems(itemData);
      setSubjects(subjectData);

      const nextPlanId = preferredPlanId
        || (planData.some((plan) => plan.id === selectedPlanId) ? selectedPlanId : planData[0]?.id || '');
      setSelectedPlanId(nextPlanId);
    } catch (error) {
      console.error('Error loading ATP data:', error);
      setStatusMessage({ type: 'error', text: 'Unable to load ATP data.' });
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedPlanId]);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadData]);

  const handlePlanDialogOpen = (plan = null) => {
    setEditingPlan(plan);
    setPlanForm(buildPlanForm(plan));
    setPlanDialogOpen(true);
  };

  const handleItemDialogOpen = (item = null) => {
    setEditingItem(item);
    const nextForm = buildItemForm(selectedPlan, item);
    setItemForm({
      ...nextForm,
      atpId: item?.atpId || selectedPlan?.id || nextForm.atpId,
      subject: item?.subject?.id || item?.subject || selectedPlan?.subject?.id || nextForm.subject,
      grade: item?.grade || selectedPlan?.grade || nextForm.grade
    });
    setItemDialogMessage({ type: '', text: '' });
    setItemDialogOpen(true);
  };

  const handleItemDialogClose = () => {
    setItemDialogOpen(false);
    setEditingItem(null);
    setItemDialogMessage({ type: '', text: '' });
    setItemForm(buildItemForm(selectedPlan));
  };

  const handlePlanInputChange = (event) => {
    const { name, value } = event.target;
    setPlanForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'subject') {
      const subject = subjects.find((entry) => entry.id === value);
      if (subject) {
        setPlanForm((prev) => ({ ...prev, grade: subject.grade || prev.grade }));
      }
    }
  };

  const handleItemInputChange = (event) => {
    const { name, value } = event.target;
    setItemForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContentItemChange = (index, value) => {
    setItemForm((prev) => ({
      ...prev,
      contentItems: prev.contentItems.map((entry, entryIndex) => (
        entryIndex === index ? value : entry
      ))
    }));
  };

  const handleAddContentField = () => {
    setItemForm((prev) => ({
      ...prev,
      contentItems: [...prev.contentItems, '']
    }));
  };

  const handleRemoveContentField = (index) => {
    setItemForm((prev) => {
      const nextContentItems = prev.contentItems.filter((_, entryIndex) => entryIndex !== index);
      return {
        ...prev,
        contentItems: nextContentItems.length ? nextContentItems : ['']
      };
    });
  };

  const handlePlanSave = async () => {
    if (!planForm.title || !planForm.subject || !planForm.grade || !planForm.academicYear) {
      return;
    }

    setDialogLoading(true);
    try {
      let activePlanId = selectedPlanId;

      if (editingPlan) {
        await updateAnnualTeachingPlanPlan(editingPlan.id, planForm);
        activePlanId = editingPlan.id;
        setStatusMessage({ type: 'success', text: 'ATP updated.' });
      } else {
        const createdPlan = await createAnnualTeachingPlanPlan(currentUser.uid, planForm);
        activePlanId = createdPlan.id;
        setStatusMessage({ type: 'success', text: 'ATP created.' });
      }

      setPlanDialogOpen(false);
      await loadData(activePlanId);
    } catch (error) {
      console.error('Error saving ATP plan:', error);
      setStatusMessage({ type: 'error', text: 'Unable to save ATP.' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleItemSave = async () => {
    if (!itemForm.atpId || !itemForm.subject || !itemForm.topic || !itemForm.term || !itemForm.plannedStart || !itemForm.plannedFinish) {
      setItemDialogMessage({ type: 'warning', text: 'Complete the topic, term, planned start, and planned finish fields before saving.' });
      return;
    }

    setDialogLoading(true);
    try {
      const payload = {
        ...itemForm,
        userId: currentUser.uid,
        content: itemForm.contentItems
          .map((entry) => entry.trim())
          .filter(Boolean)
      };

      if (editingItem) {
        await updateAnnualTeachingPlan(editingItem.id, payload);
        setStatusMessage({ type: 'success', text: 'ATP item updated.' });
      } else {
        await createAnnualTeachingPlan(currentUser.uid, payload);
        setStatusMessage({ type: 'success', text: 'ATP item added.' });
      }

      handleItemDialogClose();
      await loadData(itemForm.atpId);
    } catch (error) {
      console.error('Error saving ATP item:', error);
      setItemDialogMessage({ type: 'error', text: error?.message || 'Unable to save ATP item.' });
      setStatusMessage({ type: 'error', text: error?.message || 'Unable to save ATP item.' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handlePlanDelete = async (planId) => {
    if (!window.confirm('Delete this ATP and all linked items? This cannot be undone.')) {
      return;
    }

    try {
      await deleteAnnualTeachingPlanPlan(planId);
      setStatusMessage({ type: 'success', text: 'ATP deleted.' });
      await loadData();
    } catch (error) {
      console.error('Error deleting ATP plan:', error);
      setStatusMessage({ type: 'error', text: 'Unable to delete ATP.' });
    }
  };

  const handleItemDelete = async (item) => {
    if (!window.confirm('Delete this ATP item? This cannot be undone.')) {
      return;
    }

    try {
      await deleteAnnualTeachingPlan(item.id, item.atpId);
      setStatusMessage({ type: 'success', text: 'ATP item deleted.' });
      await loadData(item.atpId);
    } catch (error) {
      console.error('Error deleting ATP item:', error);
      setStatusMessage({ type: 'error', text: 'Unable to delete ATP item.' });
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedPlan) {
      return;
    }

    setImportFileName(file.name);

    try {
      const content = await file.text();
      const { items: parsedItems, errors } = parseImportedItems(content, selectedPlan);
      setImportPreview(parsedItems);
      setImportErrors(errors);
    } catch (error) {
      console.error('Error reading import file:', error);
      setImportPreview([]);
      setImportErrors(['The selected file could not be read.']);
    }
  };

  const handleImportSave = async () => {
    if (!selectedPlan || importPreview.length === 0 || importErrors.length > 0) {
      return;
    }

    setDialogLoading(true);
    try {
      const importCount = importPreview.length;
      await createAnnualTeachingPlansBulk(currentUser.uid, selectedPlan.id, importPreview);
      setImportDialogOpen(false);
      setImportPreview([]);
      setImportErrors([]);
      setImportFileName('');
      setStatusMessage({ type: 'success', text: `${importCount} ATP items imported.` });
      await loadData(selectedPlan.id);
    } catch (error) {
      console.error('Error importing ATP items:', error);
      setStatusMessage({ type: 'error', text: 'Unable to import ATP file.' });
    } finally {
      setDialogLoading(false);
    }
  };

  const openImportDialog = () => {
    setImportFileName('');
    setImportPreview([]);
    setImportErrors([]);
    setImportDialogOpen(true);
  };

  const renderPlanCards = () => {
    if (plans.length === 0) {
      return (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center', backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          <Typography variant="body1" color="textSecondary" mb={2}>
            No ATPs created yet.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handlePlanDialogOpen()}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
          >
            Create Your First ATP
          </Button>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {plans.map((plan) => (
          <Grid item xs={12} md={6} lg={4} key={plan.id}>
            <Card
              variant={selectedPlanId === plan.id ? 'elevation' : 'outlined'}
              sx={{
                cursor: 'pointer',
                borderColor: selectedPlanId === plan.id ? THEME_COLORS.primary : 'divider',
                boxShadow: selectedPlanId === plan.id ? '0 0 0 1px rgba(39, 35, 97, 0.2)' : 'none'
              }}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {plan.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {plan.subject?.name || 'Unknown subject'} - Grade {plan.grade}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit ATP">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); handlePlanDialogOpen(plan); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete ATP">
                      <IconButton size="small" color="error" onClick={(event) => { event.stopPropagation(); handlePlanDelete(plan.id); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                  <Chip label={plan.academicYear} size="small" />
                  <Chip label={`${plan.itemCount || 0} items`} size="small" sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }} />
                  <Chip label={plan.source === 'import' ? 'Imported' : 'Manual'} size="small" variant="outlined" />
                </Stack>

                {plan.description && (
                  <Typography variant="body2" color="textSecondary" mt={2}>
                    {plan.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderItemTable = () => {
    if (!selectedPlan) {
      return null;
    }

    if (filteredItems.length === 0) {
      return (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center', backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          <Typography variant="body1" color="textSecondary" mb={2}>
            {searchTerm || termFilter ? 'No ATP items match the current filters.' : 'No ATP items added to this plan yet.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleItemDialogOpen()}
              sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
            >
              Add ATP Item
            </Button>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={openImportDialog}>
              Import ATP File
            </Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
            <TableRow>
              <TableCell>Topic</TableCell>
              <TableCell>Term</TableCell>
              <TableCell>Planned Timeline</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <MathRenderer
                    content={item.topic}
                    className="atp-item-topic"
                    style={{ fontSize: '0.875rem', fontWeight: 500 }}
                  />
                  {normalizeContentItems(item.content).filter(Boolean).length > 0 && (
                    <Box mt={0.5}>
                      {normalizeContentItems(item.content).filter(Boolean).map((entry, index) => (
                        <MathRenderer
                          key={`${item.id}-content-${index}`}
                          content={entry}
                          className="atp-item-content"
                          style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}
                        />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={`Term ${item.term}`} size="small" />
                </TableCell>
                <TableCell>
                  {item.plannedStart} to {item.plannedFinish}
                </TableCell>
                <TableCell>{item.progress}</TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell>
                  <Chip label={item.source === 'import' ? 'Import' : 'Manual'} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => handleItemDialogOpen(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleItemDelete(item)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexDirection={{ xs: 'column', md: 'row' }} gap={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight="medium">
            Annual Teaching Plans
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Create ATPs, add curriculum items manually, or import item lists from CSV, TSV, or JSON files.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handlePlanDialogOpen()}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
          >
            New ATP
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleItemDialogOpen()} disabled={!selectedPlan}>
            Add Item
          </Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={openImportDialog} disabled={!selectedPlan}>
            Upload ATP File
          </Button>
        </Stack>
      </Box>

      {statusMessage.text && (
        <Alert severity={statusMessage.type || 'info'} sx={{ mb: 3 }} onClose={() => setStatusMessage({ type: '', text: '' })}>
          {statusMessage.text}
        </Alert>
      )}

      <Box mb={3}>
        {renderPlanCards()}
      </Box>

      {selectedPlan && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedPlan.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedPlan.subject?.name} - Grade {selectedPlan.grade} - {selectedPlan.academicYear}
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  placeholder="Search topic or content"
                  size="small"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Term</InputLabel>
                  <Select value={termFilter} label="Term" onChange={(event) => setTermFilter(event.target.value)}>
                    <MenuItem value="">All Terms</MenuItem>
                    <MenuItem value="1">Term 1</MenuItem>
                    <MenuItem value="2">Term 2</MenuItem>
                    <MenuItem value="3">Term 3</MenuItem>
                    <MenuItem value="4">Term 4</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="text" startIcon={<FilterIcon />} onClick={() => { setSearchTerm(''); setTermFilter(''); }}>
                  Clear
                </Button>
              </Stack>
            </Box>
          </Paper>

          {renderItemTable()}
        </>
      )}

      <Dialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, py: 1.5 }}>
          <Typography variant="h6" fontWeight="bold">
            {editingPlan ? 'Edit Annual Teaching Plan' : 'Create Annual Teaching Plan'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                  ATP Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="ATP Title"
                      name="title"
                      value={planForm.title}
                      onChange={handlePlanInputChange}
                      placeholder="e.g. Grade 10 Mathematics ATP"
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ minWidth: 240 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Subject</InputLabel>
                      <Select
                        name="subject"
                        value={planForm.subject}
                        label="Subject"
                        onChange={handlePlanInputChange}
                        startAdornment={
                          planForm.subject ? (
                            <InputAdornment position="start">
                              <SubjectIcon fontSize="small" />
                            </InputAdornment>
                          ) : null
                        }
                      >
                        <MenuItem value="">
                          <em>Select a subject</em>
                        </MenuItem>
                        {subjects.map((subject) => (
                          <MenuItem key={subject.id} value={subject.id}>
                            {subject.name} - Grade {subject.grade}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ minWidth: 180 }}>
                    <TextField
                      fullWidth
                      required
                      label="Grade"
                      name="grade"
                      value={planForm.grade}
                      onChange={handlePlanInputChange}
                      helperText={planForm.subject ? 'Auto-filled from subject' : 'Enter the target grade'}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ minWidth: 180 }}>
                    <TextField
                      fullWidth
                      required
                      label="Academic Year"
                      name="academicYear"
                      value={planForm.academicYear}
                      onChange={handlePlanInputChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                  Context
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Description"
                      name="description"
                      value={planForm.description}
                      onChange={handlePlanInputChange}
                      placeholder="Add notes about pacing, term structure, or curriculum intent."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(39, 35, 97, 0.03)',
                        borderColor: 'rgba(39, 35, 97, 0.12)'
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        ATP Summary
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {planForm.title || 'Untitled ATP'}
                        {' for '}
                        {subjects.find((subject) => subject.id === planForm.subject)?.name || 'No subject selected'}
                        {' - Grade '}
                        {planForm.grade || 'N/A'}
                        {' ('}
                        {planForm.academicYear || 'No year'}
                        {')'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePlanSave}
            variant="contained"
            disabled={dialogLoading || !planForm.title || !planForm.subject || !planForm.grade || !planForm.academicYear}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
          >
            {dialogLoading ? 'Saving...' : editingPlan ? 'Update ATP' : 'Create ATP'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDialogOpen} onClose={handleItemDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit ATP Item' : 'Add ATP Item'}</DialogTitle>
        <DialogContent dividers>
          {itemDialogMessage.text && (
            <Alert severity={itemDialogMessage.type || 'info'} sx={{ mb: 2 }} onClose={() => setItemDialogMessage({ type: '', text: '' })}>
              {itemDialogMessage.text}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <TextField fullWidth label="ATP" value={selectedPlan?.title || ''} disabled />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
              <TextField fullWidth label="Subject" value={selectedPlan?.subject?.name || ''} disabled />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 180 }}>
              <TextField fullWidth label="Grade" name="grade" value={itemForm.grade} onChange={handleItemInputChange} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Term</InputLabel>
                <Select name="term" value={itemForm.term} label="Term" onChange={handleItemInputChange}>
                  <MenuItem value="">
                    <em>Select a term</em>
                  </MenuItem>
                  <MenuItem value="1">Term 1</MenuItem>
                  <MenuItem value="2">Term 2</MenuItem>
                  <MenuItem value="3">Term 3</MenuItem>
                  <MenuItem value="4">Term 4</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 280 }}>
              <TextField fullWidth label="Topic" name="topic" value={itemForm.topic} onChange={handleItemInputChange} required />
            </Grid>
            <Grid item xs={12} sx={{ minWidth: 320 }}>
              <Stack spacing={1.5}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Content Items</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddContentField}>
                    Add Content Field
                  </Button>
                </Box>
                {itemForm.contentItems.map((entry, index) => (
                  <Box key={`content-item-${index}`} display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={`Content ${index + 1}`}
                      value={entry}
                      onChange={(event) => handleContentItemChange(index, event.target.value)}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveContentField(index)}
                      disabled={itemForm.contentItems.length === 1}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ minWidth: 180 }}>
              <TextField fullWidth label="Duration" name="duration" value={itemForm.duration} onChange={handleItemInputChange} />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ minWidth: 180 }}>
              <TextField
                fullWidth
                type="date"
                label="Planned Start"
                name="plannedStart"
                value={itemForm.plannedStart}
                onChange={handleItemInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ minWidth: 180 }}>
              <TextField
                fullWidth
                type="date"
                label="Planned Finish"
                name="plannedFinish"
                value={itemForm.plannedFinish}
                onChange={handleItemInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 220 }}>
              <TextField
                fullWidth
                type="date"
                label="Actual Start"
                name="actualStart"
                value={itemForm.actualStart}
                onChange={handleItemInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 220 }}>
              <TextField
                fullWidth
                type="date"
                label="Actual Finish"
                name="actualFinish"
                value={itemForm.actualFinish}
                onChange={handleItemInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Progress</InputLabel>
                <Select name="progress" value={itemForm.progress} label="Progress" onChange={handleItemInputChange}>
                  <MenuItem value="Planned">Planned</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={itemForm.status} label="Status" onChange={handleItemInputChange}>
                  <MenuItem value="On Schedule">On Schedule</MenuItem>
                  <MenuItem value="Behind Schedule">Behind Schedule</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleItemDialogClose}>Cancel</Button>
          <Button
            onClick={handleItemSave}
            variant="contained"
            disabled={dialogLoading}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
          >
            {dialogLoading ? 'Saving...' : editingItem ? 'Update Item' : 'Save Item'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload ATP File</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Import CSV, TSV, or JSON with columns `topic`, `term`, `plannedStart`, and `plannedFinish`. Optional columns include `content`, `duration`, `progress`, and `status`.
          </Alert>

          <Box mb={2}>
            <Button variant="outlined" component="label" startIcon={<DescriptionIcon />}>
              Choose File
              <input hidden type="file" accept=".csv,.tsv,.txt,.json" onChange={handleImportFileChange} />
            </Button>
            {importFileName && (
              <Typography variant="body2" color="textSecondary" mt={1}>
                {importFileName}
              </Typography>
            )}
          </Box>

          {importErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importErrors.join(' ')}
            </Alert>
          )}

          {importPreview.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Previewing {importPreview.length} ATP items for {selectedPlan?.title}
              </Typography>
              <Stack spacing={1}>
                {importPreview.slice(0, 5).map((item, index) => (
                  <Box key={`${item.topic}-${index}`} display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2">{item.topic}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Term {item.term} - {item.plannedStart} to {item.plannedFinish}
                    </Typography>
                  </Box>
                ))}
                {importPreview.length > 5 && (
                  <Typography variant="caption" color="textSecondary">
                    {importPreview.length - 5} more items will also be imported.
                  </Typography>
                )}
              </Stack>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleImportSave}
            variant="contained"
            disabled={dialogLoading || importPreview.length === 0 || importErrors.length > 0}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
          >
            {dialogLoading ? 'Importing...' : 'Import Items'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnualTeachingPlan;
